import dayjs from "../vendor/DayJs";

import * as Utils from "./Utils";
import * as StringUtils from "./StringUtils";
import { WorkingDay } from "../types/Settings";
import { Nullable } from "../types/UtilTypes";

export const ONE_WEEK = /* 1 weeks */ 604800000;

export const getAbsoluteUnixFromTimeRange = (
	dateRange: string,
	subtractWeek = false,
) => {
	const now = dayjs().utc();
	const parts = dateRange.split(" ");

	if (subtractWeek) {
		now.subtract(7, "d");
	}

	return now
		.subtract(parts[0] as any, parts[1] as any)
		.startOf(parts[1] as any)
		.valueOf();
};

const DAYS_OF_WEEK: Record<WorkingDay, number> = {
	MONDAY: 1,
	TUESDAY: 2,
	WEDNESDAY: 3,
	THURSDAY: 4,
	FRIDAY: 5,
	SATURDAY: 6,
	SUNDAY: 7,
};

export const sortDaysOfWeek = (daysOfWeek: WorkingDay[]) => {
	return daysOfWeek.sort((a, b) => {
		return (
			(DAYS_OF_WEEK[a.toUpperCase() as WorkingDay] || 0) -
			(DAYS_OF_WEEK[b.toUpperCase() as WorkingDay] || 0)
		);
	});
};

export const getTimezoneName = Utils.memoize((timezone?: string) => {
	if (Intl.DateTimeFormat) {
		const format = new Intl.DateTimeFormat(undefined, {
			timeZone: timezone,
			timeZoneName: "short",
		});

		if (format.formatToParts) {
			const part = format
				.formatToParts()
				.find((part) => part.type === "timeZoneName");
			if (part) {
				return part.value;
			}
		}
	}

	const today = new Date();

	if (!today.toLocaleDateString) {
		return null;
	}
	const short = today.toLocaleDateString(undefined, {
		timeZoneName: "short",
		timeZone: timezone,
	});
	const full = today.toLocaleDateString(undefined, {
		timeZoneName: "long",
		timeZone: timezone,
	});

	let diffIndex = -1;
	for (let i = 0; i < short.length; i++) {
		if (short[i] !== full[i]) {
			diffIndex = i;
			break;
		}
	}
	diffIndex -= 1;
	if (diffIndex === -1) {
		return null;
	}

	return short.slice(diffIndex, short.indexOf(" ", diffIndex) + 1);
});

const getDuration = (
	value: Nullable<string | number>,
	from: number | undefined,
	to: number | undefined,
) => {
	if (typeof value === "number") {
		return value;
	}

	if (typeof from !== "number" && typeof to !== "number") {
		return null;
	}
	if (!from && from !== undefined) {
		return null;
	}
	if (!to && to !== undefined) {
		return null;
	}

	let fromTimestamp = from ? from : Date.now();
	let toTimestamp = to ? to : Date.now();

	if (fromTimestamp.valueOf) {
		fromTimestamp = fromTimestamp.valueOf();
	}

	if (toTimestamp.valueOf) {
		toTimestamp = toTimestamp.valueOf();
	}

	return toTimestamp - fromTimestamp;
};

export interface FormatDurationOptions {
	value?: Nullable<number | string>;
	from?: number;
	to?: number;
	ago?: boolean;
	format?: string;
}

export const formatDuration = (options: FormatDurationOptions = {}) => {
	const { value, from, to, ago, format } = options;
	const duration = getDuration(value, from, to);

	if (duration === null) {
		return duration;
	}

	if (format) {
		const seconds = Math.abs(duration) / 1000; // seconds
		const mins = seconds === 0 ? 0 : seconds / 60;
		const hours = mins === 0 ? 0 : mins / 60;
		const days = hours === 0 ? 0 : hours / 24;

		let replaceIndex = 0;
		const replaces: Record<string, string> = {};

		let extractedString = format.replace(/\[(.*?)]/g, (match) => {
			const index = replaceIndex++;
			replaces[index] = match[1];
			return `$${index}`;
		});

		const hasHours = extractedString.includes("h");
		const hasMins = extractedString.includes("m");
		const hasSeconds = extractedString.includes("s");

		if (hasHours) {
			const hoursValue = Math.floor(hours);
			extractedString = extractedString
				.replace("hh", StringUtils.padNumber(hoursValue, 2))
				.replace("h", hoursValue.toString());
		}

		if (hasMins) {
			const minsValue = Math.floor(hasHours ? mins % 60 : mins);

			extractedString = extractedString
				.replace("mm", StringUtils.padNumber(minsValue, 2))
				.replace("m", minsValue.toString());
		}

		if (hasSeconds) {
			const secondsValue = Math.floor(hasMins ? seconds % 60 : seconds);

			extractedString = extractedString
				.replace("ss", StringUtils.padNumber(secondsValue, 2))
				.replace("s", secondsValue.toString());
		}

		Object.values(replaces).forEach((replace, index) => {
			extractedString = extractedString.replace(
				new RegExp(`\\$${index}`, "g"),
				replace,
			);
		});

		return extractedString;
	}

	if (duration === 0) {
		return null;
	}

	return duration > 0
		? dayjs().from(Date.now() + duration, !ago)
		: dayjs().to(Date.now() - duration, !ago);
};
